import { makeAwsSignedRequest } from '../ApiService';

const BATCHER_BASE = 'batcher-api';

export interface ParameterInfo {
    name: string;
    type: string;
    description: string;
}

/** Mirrors FunctionMetadata returned by ListFunctionsRequestHandler (GET /batcher-api/functions). */
export interface FunctionMetadata {
    functionName: string;
    functionIdentifier: string;
    aiContext: string;
    inputSchema: ParameterInfo[];
    additionalArguments: ParameterInfo[];
    outputSchema: ParameterInfo[];
    flatMap: boolean;
    requiresAuthentication: boolean;
}

/** One entry in the function chain sent to SubmitJobRequestHandler (POST /batcher-api/jobs). */
export interface FunctionChainItem {
    functionIdentifier: string;
    additionalArguments: Record<string, unknown>;
}

/** Mirrors JobResultResponse from GetJobResultHandler (GET /batcher-api/jobs/{jobId}). */
export interface JobResult {
    /** SFN execution status, e.g. RUNNING, SUCCEEDED, FAILED, TIMED_OUT, ABORTED. */
    status: string;
    /** Presigned download URL for the result CSV — present only when status is SUCCEEDED. */
    downloadUrl?: string;
    /** Error message — present when status is FAILED or TIMED_OUT. */
    error?: string;
}

/** Job statuses that end polling (no further status change expected). */
export const TERMINAL_JOB_STATUSES = ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'];

/** Derive the S3 object key (fileName) from a presigned upload URL's last path segment. */
const fileNameFromPresignedUrl = (presignedUrl: string): string => {
    const { pathname } = new URL(presignedUrl);
    const segment = pathname.split('/').filter(Boolean).pop() || '';
    return decodeURIComponent(segment);
};

export const BatcherService = {
    /** Fetch the list of available batcher functions with their metadata. */
    listFunctions: (stage: string, realm: string): Promise<FunctionMetadata[]> =>
        makeAwsSignedRequest(stage, realm, `${BATCHER_BASE}/functions`),

    /** Request a presigned S3 PUT URL for the input CSV. Returns the URL as plain text. */
    getPresignedUploadUrl: (stage: string, realm: string): Promise<string> =>
        makeAwsSignedRequest(stage, realm, `${BATCHER_BASE}/presigned-url`, { method: 'POST' }),

    /** Upload CSV content directly to the presigned S3 URL (the URL is already signed). */
    uploadCsv: async (presignedUrl: string, csv: string): Promise<void> => {
        const response = await fetch(presignedUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'text/csv' },
            body: csv,
        });
        if (!response.ok) {
            throw new Error(`Failed to upload CSV to S3 (HTTP ${response.status})`);
        }
    },

    /** Poll a job's status and (once succeeded) its result download URL. */
    getJobResult: (stage: string, realm: string, jobId: string): Promise<JobResult> =>
        makeAwsSignedRequest(stage, realm, `${BATCHER_BASE}/jobs/${encodeURIComponent(jobId)}`),

    /**
     * Submit a batch job for the uploaded file and function chain.
     * On success returns `{ jobId }`; on validation/authorization failure the backend
     * returns a plain error string (e.g. "Access denied: ..."), so the response is typed
     * loosely and interpreted by the caller.
     */
    submitJob: (
        stage: string,
        realm: string,
        fileName: string,
        functionChain: FunctionChainItem[],
    ): Promise<{ jobId?: string } | string> =>
        makeAwsSignedRequest(stage, realm, `${BATCHER_BASE}/jobs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileName, functionChain }),
        }),

    /**
     * Full submit flow: request a presigned URL, upload the CSV, then submit the job.
     * Returns the created jobId, or throws an Error carrying the backend's raw response
     * (e.g. an authorization message) when no jobId is returned.
     */
    uploadAndSubmit: async (
        stage: string,
        realm: string,
        csv: string,
        functionChain: FunctionChainItem[],
    ): Promise<string> => {
        const presignedUrl = await BatcherService.getPresignedUploadUrl(stage, realm);
        await BatcherService.uploadCsv(presignedUrl, csv);
        const fileName = fileNameFromPresignedUrl(presignedUrl);
        const response = await BatcherService.submitJob(stage, realm, fileName, functionChain);

        const jobId = typeof response === 'object' && response !== null ? response.jobId : undefined;
        if (!jobId) {
            // Not a job ID — surface whatever the backend returned so the user can see it.
            const detail = typeof response === 'string' ? response : JSON.stringify(response);
            throw new Error(detail || 'Job submission failed: no job ID returned');
        }
        return jobId;
    },
};
